import React, { useState, useEffect, useMemo, useCallback } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Card, CardBody, CardTitle, CardSubtitle } from "reactstrap";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d"];

// Custom shape for bars that returns null if the value is 0 or undefined.
const renderCustomBar = (props) => {
  const { x, y, width, height, value, fill } = props;
  if (!value || value === 0) {
    return null;
  }
  return <rect x={x} y={y} width={width} height={height} fill={fill} />;
};

const StatusChart = ({ statusCounts = {} }) => {
  const [chartHeight, setChartHeight] = useState(window.innerWidth < 768 ? 250 : 300);

  // Responsive height adjustment
  useEffect(() => {
    const handleResize = () => setChartHeight(window.innerWidth < 768 ? 250 : 300);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Process data: Create regionalData and collect valid status types (only if they exist in at least one region)
  const { regionalData, validStatusTypes } = useMemo(() => {
    const regionalData = Object.entries(statusCounts).map(([regionName, statuses]) => ({
      regionName,
      ...statuses,
    }));
    const allStatusTypes = new Set();
    regionalData.forEach(region => {
      Object.keys(region).forEach(key => key !== "regionName" && allStatusTypes.add(key));
    });

    // Only include statuses that have at least one non-zero value in any region
    const validStatusTypes = Array.from(allStatusTypes).filter(status =>
      regionalData.some(region => region[status] > 0)
    );

    return { regionalData, validStatusTypes };
  }, [statusCounts]);


  // Custom tooltip remains the same
  const CustomTooltip = useCallback(({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div
        className="custom-tooltip"
        style={{
          backgroundColor: "#fff",
          padding: "10px",
          border: "1px solid #ccc",
          borderRadius: "4px",
          boxShadow: "0 2px 5px rgba(0,0,0,0.15)",
        }}
      >
        <p className="label" style={{ fontWeight: "bold", marginBottom: "5px" }}>{`Region: ${label}`}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ margin: "2px 0", color: entry.color }}>{`${entry.name}: ${entry.value}`}</p>
        ))}
      </div>
    );
  }, []);

  if (!regionalData.length) {
    return (
      <Card className="text-center">
        <CardBody>
          <p className="text-muted">No status data available.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="mb-4">
      <CardBody>
        <CardTitle tag="h5" className="mb-4 text-center">Status Distribution</CardTitle>
        <div style={{ width: "100%", height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={regionalData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <XAxis
                dataKey="regionName"
                tick={{ fontSize: window.innerWidth < 768 ? 10 : 12 }}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis tick={{ fontSize: window.innerWidth < 768 ? 10 : 12 }} />
              <Tooltip content={CustomTooltip} />
              <Legend wrapperStyle={{ fontSize: "12px" }} verticalAlign="top" align="center" />

              {validStatusTypes.map((status, index) => (
                <Bar
                  key={status}
                  dataKey={status}
                  fill={COLORS[index % COLORS.length]}
                  barSize={40}
                  shape={renderCustomBar}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardBody>
    </Card>
  );
};

export default StatusChart;
